import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { code: "", label: "", description: "", cost: 0, free_over: 0, active: true, sort_order: 0 };

export default function AdminShipping() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/shipping-rates").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, sort_order: items.length }); setEditing(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r.code); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...form, cost: Number(form.cost || 0), free_over: Number(form.free_over || 0), sort_order: Number(form.sort_order || 0) };
    try {
      if (editing) await api.patch(`/admin/shipping-rates/${editing}`, body);
      else await api.post("/admin/shipping-rates", body);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete rate ${code}?`)) return;
    await api.delete(`/admin/shipping-rates/${code}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-shipping-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Shipping</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Shipping rates</h1>
          <p className="mt-1 text-sm text-slate-600">Flat rate, pickup, or "free over X." Real integrations plug in later.</p>
        </div>
        <Button onClick={openNew} data-testid="new-shipping-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1"/> New rate</Button>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
            <tr><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Label</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Cost</th><th className="text-right px-4 py-2">Free over</th><th className="text-left px-4 py-2">Active</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.code} className="border-b border-slate-100 last:border-b-0" data-testid={`shipping-row-${r.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                <td className="px-4 py-3">{r.label}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.description}</td>
                <td className="px-4 py-3 text-right font-mono">${Number(r.cost).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono">{r.free_over ? `$${Number(r.free_over).toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3">{r.active ? <span className="text-blue-600">●</span> : <span className="text-slate-300">●</span>}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(r)} className="p-1 rounded hover:bg-slate-100"><Pencil className="w-3.5 h-3.5 text-slate-500"/></button>
                  <button onClick={() => remove(r.code)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-500"/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit rate" : "New rate"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <Input required placeholder="Code (e.g., flat)" value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} disabled={!!editing} data-testid="ship-code"/>
            <Input required placeholder="Label" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} data-testid="ship-label"/>
            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" step="0.01" placeholder="Cost" value={form.cost} onChange={(e) => setForm({...form, cost: e.target.value})} data-testid="ship-cost"/>
              <Input type="number" step="0.01" placeholder="Free over (0 = no)" value={form.free_over} onChange={(e) => setForm({...form, free_over: e.target.value})}/>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({...form, active: e.target.checked})}/> Active</label>
              <Input type="number" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({...form, sort_order: e.target.value})} className="w-32"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
